import {
  Component,
  Inject,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { IndustriesModel } from '../../admin/industries/industries.model';
import { UtilsService } from '../../shared/utils.service';
import { ApiRequestService } from '../../shared/api-request.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToolModel } from '../tools.model';
import { NgForm } from '@angular/forms';
import moment from 'moment';
import { AppService } from '../../app.service';
import { RBACService } from '../../shared/rbac.service';

@Component({
  selector: 'app-tools-edit',
  templateUrl: './tools-edit.component.html',
  styleUrls: ['./tools-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ToolsEditComponent implements OnInit {
  inProgress: boolean = false;

  industries: IndustriesModel[] = [];

  expiresAtDateTime: moment.Moment | null;

  selectedFiles: File[] = [];

  // RBAC permission flags. Resolved once in ngOnInit() to avoid re-evaluating on every change detection cycle.
  canDelete: boolean = false;

  constructor(
    public utils: UtilsService,
    @Inject(MAT_DIALOG_DATA) public tool: ToolModel,
    private dialogRef: MatDialogRef<ToolsEditComponent>,
    private api: ApiRequestService,
    private app: AppService,
    public rbac: RBACService
  ) {}

  ngOnInit() {
    // Resolve RBAC permission flags.
    this.canDelete = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_REGISTERS_TOOLS,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );

    // Check if the tool id is present. If so, load it from the API.
    if (this.tool.id) {
      this.find(this.tool.id);
    }

    this.getIndustries();
  }

  private async getIndustries() {
    this.industries = await this.utils.getIndustries();
  }

  onSubmit(form: NgForm, autoCloseDialog: boolean = true): void {
    // Check if the button was already pressed.
    if (this.inProgress) {
      return;
    }

    // Check form validation.
    if (!form.valid) {
      this.utils.showFormValidationError('Please enter all required features.');
      return;
    }

    this.inProgress = true;

    // Convert the selected date and time to a unix timestamp.
    if (this.expiresAtDateTime && this.expiresAtDateTime instanceof moment) {
      const expiresAtDateTime: moment.Moment = moment.tz(
        this.expiresAtDateTime.format('M/D/YYYY, h:mm:ss a'),
        'M/D/YYYY, h:mm:ss a',
        this.app.account.timezone
      );
      this.tool.expires_at = expiresAtDateTime.unix();
    } else {
      this.tool.expires_at = null;
    }

    !this.tool.id ? this.create(autoCloseDialog) : this.update(autoCloseDialog);
  }

  /**
   * Finds a tool by its ID.
   *
   * This method makes a request to the API to retrieve the tool information and stores it in the component.
   *
   * @param {number} id - The ID of the tool.
   *
   * @private
   *
   * @return {void}
   */
  private find(id: number) {
    // Make the API request.
    this.api
      .makeRequest('get', `v2/tools/${id}`)
      .then((response) => {
        // Store the response.
        this.tool = response as ToolModel;

        // Convert the unix "expires_at" timestamp to a moment date/time object.
        if (this.tool.expires_at) {
          this.expiresAtDateTime = moment.unix(this.tool.expires_at);
        }

        // Industry - Default to zero - All
        if (this.tool.industry_id == null) {
          this.tool.industry_id = 0;
        }
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  onDelete(): void {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_REGISTERS_TOOLS,
            RBACService.RBAC_A.A_DELETE
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to archive tools.'
      );
      return;
    }

    this.utils.showModal(
      'Remove Tool',
      `Are you sure you want to archive "${this.tool.title}"?`,
      () => {
        this.api
          .makeRequest('delete', `v2/tools/${this.tool.id}`)
          .then((response) => {
            this.utils.showToast(`"${this.tool.title}" has been archvied.`);
            this.dialogRef.close(true);
          })
          .catch((errorResponse) => {
            this.utils.handleAPIErrors(errorResponse);
          });
      }
    );
  }

  private update(autoCloseDialog: boolean = true): void {
    this.api
      .makeRequest('put', `v2/tools/${this.tool.id}`, this.tool)
      .then((response: ToolModel) => {
        this.tool = response as ToolModel;
        this.onSuccess(`Updated: "${response.title}"`, autoCloseDialog);
        this.inProgress = false;
      });
  }

  private create(autoCloseDialog: boolean = true): void {
    this.api
      .makeRequest('post', 'v2/tools', this.tool)
      .then((response: ToolModel) => {
        this.tool = response as ToolModel;

        // Auto-close dialog if user lacks edit permission: they can't do anything else useful.
        const hasEditPermission = this.rbac.userCanAccess(
          [
            RBACService.getPermission(
              RBACService.RBAC_F.F_REGISTERS_TOOLS,
              RBACService.RBAC_A.A_EDIT
            )
          ],
          true
        );
        if (!hasEditPermission) {
          this.utils.showToast(`New Tools "${response.title}" created.`);
          this.dialogRef.close(response);
          return;
        }

        this.onSuccess(`New Tools "${response.title}" created.`);
      })
      .finally(() => {
        // Upload files.
        this.uploadSelectedFiles(
          this.tool.id,
          (): void => {
            this.inProgress = false;
          }
        );
      });
  }

  private onSuccess(message: string, autoCloseDialog: boolean = true): void {
    this.utils.showToast(message);
    // Check if the dialog should be closed.
    if (autoCloseDialog) {
      this.dialogRef.close(this.tool);
    }
  }

  /**
   * Uploads selected files to the server.
   *
   * @returns {void}
   */
  private uploadSelectedFiles(id: number, callback?: any): void {
    if (id && this.selectedFiles.length > 0) {
      this.api
        .makeUploadRequest(`v2/file-manager/tool/${id}`, this.selectedFiles)
        .then(
          (response: any): void => {
            this.utils.showToast('Your files successfully uploaded.');
          }
        )
        .finally(
          (): void => {
            this.selectedFiles.length = 0;
            if (typeof callback === 'function') {
              callback();
            }
          }
        );
    }
  }

  /**
   * If it is a new safety observation, add the files to the selected files array.
   * Existing safety observation files will be handled by the file manager.
   * @param fileList
   */
  onSelectFiles(fileList: FileList): void {
    const files: File[] = Array.from(fileList);
    if (files && files.length > 0) {
      this.selectedFiles.push(...files);
    }
  }

  /**
   * Retrieves the details of the output date and time based on the provided event.
   * @param {any} event - The event object containing the output date and time details.
   * @return {void}
   */
  getOutputDateTimeDetails(event: any) {
    this.expiresAtDateTime = event;
  }

  // Expose RBACService to template for inline permission checks.
  protected readonly RBACService = RBACService;
}
